/**
 * Report Generate Page
 *
 * Bare-bones analyst UI for manually triggering monthly-report generation
 * against a chosen tenant + period. Intentionally ugly/functional for
 * prompt iteration — we'll layer on section toggles, incident pickers,
 * overrides, and the magazine preview in later steps.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { getTenants } from '../services/tenants';
import type { Tenant } from '../data/types';
import {
  generateReport,
  type GenerateReportResponse,
  type FeaturedIncidentInput,
  type ServiceDelivery,
} from '../services/reportGeneration';
import { IncidentCuration } from '../components/reporting/IncidentCuration';
import { IndustryUpdateCuration } from '../components/reporting/IndustryUpdateCuration';
import { ServiceDeliveryForm } from '../components/reporting/ServiceDeliveryForm';

// ============================================================================
// Defaults
// ============================================================================

function defaultPeriod(): { start: string; end: string } {
  // Previous calendar month in UTC.
  const now = new Date();
  const endOfPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const startOfPrev = new Date(
    Date.UTC(endOfPrev.getUTCFullYear(), endOfPrev.getUTCMonth() - 1, 1),
  );
  return {
    start: startOfPrev.toISOString().slice(0, 10),
    end: endOfPrev.toISOString().slice(0, 10),
  };
}

// ============================================================================
// Component
// ============================================================================

export function ReportGenerate() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState<string | null>(null);

  const [tenantId, setTenantId] = useState<string>('');
  const initialPeriod = defaultPeriod();
  const [periodStart, setPeriodStart] = useState(initialPeriod.start);
  const [periodEnd, setPeriodEnd] = useState(initialPeriod.end);
  const [customInstructions, setCustomInstructions] = useState('');

  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<GenerateReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [featured, setFeatured] = useState<FeaturedIncidentInput[]>([]);
  const [serviceDelivery, setServiceDelivery] = useState<ServiceDelivery | undefined>(undefined);
  const [industryUpdateIds, setIndustryUpdateIds] = useState<string[]>([]);

  const periodStartIso = periodStart
    ? new Date(periodStart + 'T00:00:00.000Z').toISOString()
    : '';
  const periodEndIso = periodEnd ? new Date(periodEnd + 'T00:00:00.000Z').toISOString() : '';

  // Load tenants once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTenantsLoading(true);
      const res = await getTenants();
      if (cancelled) return;
      if (res.success) {
        const active = res.data.filter((t) => (t.onboardingState ?? '').toLowerCase() === 'active');
        setTenants(active);
        if (!tenantId && active.length > 0) setTenantId(active[0].tenantId);
      } else {
        setTenantsError(res.error ?? 'Failed to load tenants');
      }
      setTenantsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit =
    !!tenantId &&
    !!periodStart &&
    !!periodEnd &&
    new Date(periodEnd) > new Date(periodStart) &&
    featured.length > 0 &&
    !generating;

  async function onGenerate() {
    setError(null);
    setResult(null);
    setGenerating(true);
    try {
      const res = await generateReport({
        tenantId,
        periodStart: periodStartIso,
        periodEnd: periodEndIso,
        customInstructions: customInstructions.trim() || undefined,
        featuredIncidents: featured.length > 0 ? featured : undefined,
        serviceDelivery,
        // Always sent, empty included: an empty pick means "no industry
        // section", not "fall back to whatever the dates match".
        industryUpdateIds,
      });
      if (res.success) {
        // Hand off to the rich vetting surface (magazine preview + checklist
        // + custom-content + Approve / Reject / Send / Delete / Regenerate).
        navigate(`/reports/${res.data.reportId}`);
        return;
      } else {
        setError(res.error ?? 'Generation failed');
      }
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-brand-blue" />
          Generate Report
        </h1>
        <p className="text-text-secondary text-sm mt-1">
          Manually trigger the monthly-report writing agent for a tenant and
          period. Typically takes 30-60 seconds.
        </p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-text-primary mb-1">
                Tenant
              </label>
              {tenantsLoading ? (
                <div className="text-sm text-text-secondary flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading tenants...
                </div>
              ) : tenantsError ? (
                <div className="text-sm text-red-600 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> {tenantsError}
                </div>
              ) : (
                <select
                  className="w-full px-3 py-2 rounded-lg bg-bg-primary border border-border text-text-primary text-sm"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                >
                  {tenants.map((t) => (
                    <option key={t.tenantId} value={t.tenantId}>
                      {t.displayName} ({t.tenantId})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Period start
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 rounded-lg bg-bg-primary border border-border text-text-primary text-sm"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Period end
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 rounded-lg bg-bg-primary border border-border text-text-primary text-sm"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-text-primary mb-1">
                Custom instructions (optional)
              </label>
              <textarea
                rows={4}
                placeholder="Anything the writing agent should emphasise, tone notes, narrative framing, etc. Wired but not yet consumed by the agent."
                className="w-full px-3 py-2 rounded-lg bg-bg-primary border border-border text-text-primary text-sm font-mono"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
              />
            </div>
          </div>

          {tenantId && periodStartIso && periodEndIso && (
            <div className="pt-2 border-t border-border">
              <IncidentCuration
                tenantId={tenantId}
                periodStart={periodStartIso}
                periodEnd={periodEndIso}
                onChange={setFeatured}
              />
            </div>
          )}

          {tenantId && (
            <div className="pt-2 border-t border-border">
              <IndustryUpdateCuration
                tenantId={tenantId}
                onChange={setIndustryUpdateIds}
              />
            </div>
          )}

          <div className="pt-2 border-t border-border">
            <ServiceDeliveryForm onChange={setServiceDelivery} />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            {featured.length > 0 ? (
              <span className="text-xs text-text-secondary">
                {featured.length} featured incident{featured.length === 1 ? '' : 's'} selected
              </span>
            ) : (
              <span className="text-xs text-text-secondary">
                Select at least one featured incident to generate
              </span>
            )}
            {error && (
              <span className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> {error}
              </span>
            )}
            <Button onClick={onGenerate} disabled={!canSubmit} variant="primary">
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" /> Generate
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-baseline justify-between">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">
                  {result.reportTitle}
                </h2>
                <p className="text-xs text-text-secondary font-mono">
                  reportId {result.reportId} · runId {result.runId} · tokens{' '}
                  {result.tokenCount ?? '–'} · {result.durationMs}ms
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-text-primary mb-2">
                Authored sections
              </h3>
              <pre className="text-xs bg-bg-primary border border-border rounded-lg p-4 overflow-auto max-h-[500px] whitespace-pre-wrap">
                {JSON.stringify(result.authoredSections, null, 2)}
              </pre>
            </div>

            <details>
              <summary className="text-sm font-medium text-text-primary cursor-pointer">
                Structured data (inputs the agent saw)
              </summary>
              <pre className="text-xs bg-bg-primary border border-border rounded-lg p-4 overflow-auto max-h-[400px] mt-2 whitespace-pre-wrap">
                {JSON.stringify(result.structuredData, null, 2)}
              </pre>
            </details>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
